import { createContext, useContext } from 'react'

// Who is signed in, available anywhere without prop-drilling. The API is the boundary — every
// role check here is cosmetic, and the same check exists server-side on the route it hides. What
// this buys is a page that doesn't offer a coach's button to a player and then 403 them for
// pressing it.

export type Role = 'admin' | 'coach' | 'player'

export interface SessionUser {
  steamid64: string
  role: Role
  name?: string
}

export interface SessionInfo {
  authed: boolean
  user: SessionUser | null
  /** Steam is the only way in since 31 Jul; false means SITE_ORIGIN isn't configured. */
  methods: { steam: boolean }
}

const RANK: Record<Role, number> = { player: 1, coach: 2, admin: 3 }

export const SessionContext = createContext<SessionInfo | null>(null)

export function useSession(): SessionInfo {
  return useContext(SessionContext) ?? { authed: false, user: null, methods: { steam: false } }
}

/** "May they do coach things" — regenerate AI, delete demos and folders, tag matches. */
export function useCanCoach(): boolean {
  const { user } = useSession()
  return !!user && RANK[user.role] >= RANK.coach
}

export function useIsAdmin(): boolean {
  const { user } = useSession()
  return user?.role === 'admin'
}
